/**
 * @file Rules Component
 * @description Handles the display and acknowledgment of exam rules and lockdown options.
 * Manages the rules acknowledgment flow before an exam begins.
 * @module context/session/components/Rules
 */

import React, { useEffect, useState } from 'react';
import { applyModalityOptions, toHumanReadable } from '../../helpers/helpers';
import {
    Flex,
    View,
} from '@smarterservices/smarter-ui';
import { useAppStore } from '../../stores/app';
import SinglePane from '../SinglePane';
import { usePaneViewer } from '../PaneViewer';
import { BlockRenderer, Blocks, Elements } from '@shared/components/BlockKit';

/**
 * Rules Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.stepData - Configuration and content for the rules step
 * @param {Array} props.stepData.body - Main content/instructions to display
 * @param {Array} props.stepData.lockdownOptions - Available lockdown options
 * @param {string} props.stepData.button - Text for the action button
 * @param {Function} props.setIsTaskComplete - Callback to notify parent when task is complete
 * @returns {React.ReactElement} The Rules component
 */
const Rules = ({ stepData, setIsTaskComplete }) => {
    // State for loading indicator
    const [isLoading, setIsLoading] = useState(true);
    // State for lockdown options from the session
    const [lockdownOptions, setLockdownOptions] = useState(null);
    // Tracks if lockdown has been successfully applied
    const [lockdownApplied, setLockdownApplied] = useState(false);
    // Tracks if user has acknowledged the rules
    const [rulesAcknowledged, setRulesAcknowledged] = useState(false);
    // Stores data about the current session
    const [sessionStartData, setSessionStartData] = useState(null);
    // List of enabled lockdown options
    const [enabledOptions, setEnabledOptions] = useState([]);
    // Generated set of rules (currently not used in render)
    const [ruleSet, setRuleSet] = useState([]);
    // Hook for navigation between panes
    const { onNext, setHideTitle } = usePaneViewer();
    // Template replacements for dynamic content in rules
    const [replacements, setReplacements] = useState({});

    // Parse rules blocks from step data
    const rulesBlocks = Blocks.fromJson(stepData.body);

    const { getSessionStartData } = useAppStore();

    useEffect(() => {
        setHideTitle(false);
    }, [setHideTitle]);

    useEffect(() => {
        /**
       * Fetches and initializes lockdown options for the session
       * @async
       */
        const initLockdownOptions = async () => {
            const sessionStartData = await getSessionStartData();
            setSessionStartData(sessionStartData);

            const options = sessionStartData.modalityOptions || {};

            // Filter and transform options based on the stepData.lockdownOptions
            const enabledOptions = stepData.lockdownOptions
                .filter((option) => options[option.key])
                .map((option) => ({
                    name: option.key,
                    displayText: option.displayText,
                    ...options[option.key],
                }));

            setLockdownOptions(enabledOptions);
            setEnabledOptions(enabledOptions);
        };

        initLockdownOptions();
    }, []);

    // Regenerate rule set when enabled options change
    useEffect(() => {
        generateFullRuleSet();
    }, [enabledOptions]);

    /**
     * Handles navigation to the next step
     * Applies lockdown options if it's the last step
     * @async
     */
    const handleNext = async () => {
        try {
            const success = await applyModalityOptions(lockdownOptions, false);
            if (success) {
                setLockdownApplied(true);
                setIsTaskComplete(true);
                // since lockdown was successful, move to next step...
                onNext();
            } else {
                //TODO: Handle case where lockdown application fails...
                console.log('Unsuccessful in applying lockdown...');
            }
        } catch (error) {
            console.log('Unsuccessful in applying lockdown...');
        }
    };

    /**
     * Handles rules acknowledgment checkbox change
     * @param {Object} e - Change event from checkbox
     */
    const handleAcknowledge = (e) => {
        setRulesAcknowledged(e.target.checked);
    };

    /**
     * Generates the complete rule set including lockdown options and permitted items
     * Updates the replacements state for template rendering
     */
    const generateFullRuleSet = () => {
        if (!sessionStartData) return;
        // Use the wizard block directly from stepData.body
        const rules = stepData.body[0];

        const lockdownItemsArr = [];
        // Create an array of ListItem elements
        enabledOptions.forEach(option => {
            lockdownItemsArr.push(
                Elements.ListItem().text(option.displayText)
            );
        });

        let lockdownOptionsBlock = Blocks.Section()
            .elements(
                Elements.Text().text('Nothing will be locked down on your computer during this exam.')
            )

        if (lockdownItemsArr.length > 0) {
            lockdownOptionsBlock = Blocks.BulletList().items(...lockdownItemsArr);
        }

        // Add a section for Permitted items and push the info that is permitted...
        const permittedItems = Object.entries(sessionStartData?.permittedItems || {}).map(
            ([key, value]) => {
                // if the key is calculator and a virtual one is allowed, let's note that....
                if (key === 'calculator' && sessionStartData?.virtualCalculatorType) {
                    return {
                        item: `${toHumanReadable(key)} (virtual calculator provided)`,
                        notes: value?.notes || ''
                    };
                } else {
                    return {
                        item: toHumanReadable(key),
                        notes: value?.notes || ''
                    };
                }
            }
        );

        let permittedItemsBlock = Blocks.Section()
            .elements(
                Elements.Text().text(stepData?.noPermittedItemsText || 'No permitted items')
            )

        if (permittedItems.length > 0) {

            // Create a bullet list with permitted items using the builder pattern
            const itemsArr = [];
            // Create an array of ListItem elements
            permittedItems.forEach(item => {
                itemsArr.push(
                    Elements.ListItem().text(`${item.item}${item.notes ? `: ${item.notes}` : ''}`)
                );
            });

            // Use the items() method with the array of ListItem elements
            // With our updates to BlockReplacement, we can now pass the builder directly
            permittedItemsBlock = Blocks.BulletList().items(...itemsArr);
        }

        setReplacements({
            course: sessionStartData?.courseTitle,
            exam: sessionStartData?.examTitle,
            duration: sessionStartData?.examDuration,
            notes: sessionStartData?.studentNotes,
            permittedItems: permittedItemsBlock,
            lockdownOptions: lockdownOptionsBlock
        })
    };

    // Main render method
    return (
        <SinglePane>
            <Flex as="div" direction="column" alignItems="center" justifyContent="center" height="100%">
                <View
                    as="div"
                    className="rules-and-protocol-content"
                    width="90%"
                >
                    <BlockRenderer
                        blocks={rulesBlocks}
                        replacements={replacements}
                        onWizardComplete={() => {
                            setRulesAcknowledged(true);
                            handleNext();
                        }}
                    />
                </View>
            </Flex>
        </SinglePane>
    );
};

/**
 * @exports Rules
 * @description Default export of the Rules component
 */
export default Rules;
